/**
 * Live Translate 双向同声传译页面
 *
 * 架构特点：
 * - 双通道并发 (Dual-Channel Routing)：同时开启两个 WebSocket 对应两种语言
 * - 自动语种切换：利用 echo_target_language=False 与后端拦截，双通道自动回避冲突
 * - 频道隔离智能拼接：支持较长对话的文本归档
 */

import { useState, useRef, useCallback, useEffect } from 'react'

// =============================================================================
// 类型定义与常量配置
// =============================================================================

interface TranscriptionMessage {
  type: 'transcription'
  input_transcription?: string
  output_transcription?: string
}

interface HistoryRecord {
  id: string
  channelId: number
  inputText: string
  outputText: string
  timestamp: number
}

const LANGUAGES = [
  { code: 'zh-Hans', label: '中文 (Mandarin)' },
  { code: 'ja', label: '日语 (日本語)' },
  { code: 'en', label: '英语 (English)' },
  { code: 'fr', label: '法语 (Français)' },
  { code: 'es', label: '西班牙语 (Español)' },
  { code: 'de', label: '德语 (Deutsch)' },
  { code: 'ko', label: '韩语 (한국어)' },
  { code: 'ru', label: '俄语 (Русский)' },
  { code: 'it', label: '意大利语 (Italiano)' },
]

const PLAYBACK_SAMPLE_RATE = 24000

// =============================================================================
// 智能文本无缝拼接算法
// =============================================================================

function mergeText(oldText: string, newText: string): string {
  if (!newText) return oldText
  if (!oldText) return newText
  if (oldText === newText) return oldText

  // 如果新片段包含了旧片段（递增式生长）
  if (newText.startsWith(oldText)) return newText

  // 如果旧片段已经包含了新片段（冗余片段）
  if (oldText.endsWith(newText)) return oldText

  // 智能重叠检测：检查旧句子的结尾是否与新句子的开头重叠
  const minLen = Math.min(oldText.length, newText.length)
  for (let i = minLen; i > 0; i--) {
    if (oldText.endsWith(newText.substring(0, i))) {
      return oldText + newText.substring(i)
    }
  }

  // 纯净的新片段，直接追加
  const needsSpace = /[a-zA-Z0-9.,?!]$/.test(oldText) && /^[a-zA-Z0-9]/.test(newText)
  return oldText + (needsSpace ? ' ' : '') + newText
}

// =============================================================================
// 音频播放器
// =============================================================================

class TranslateAudioPlayer {
  private audioContext: AudioContext | null = null
  private nextPlayTime = 0

  async unlock(): Promise<void> {
    const AC = window.AudioContext || (window as any).webkitAudioContext
    if (!this.audioContext) {
      this.audioContext = new AC({ sampleRate: PLAYBACK_SAMPLE_RATE })
    }
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
    }
  }

  playChunk(pcmData: ArrayBuffer): void {
    if (!this.audioContext) return

    try {
      const int16Data = new Int16Array(pcmData)
      const audioBuffer = this.audioContext.createBuffer(1, int16Data.length, PLAYBACK_SAMPLE_RATE)
      const channelData = audioBuffer.getChannelData(0)
      for (let i = 0; i < int16Data.length; i++) {
        channelData[i] = int16Data[i] / 32768.0
      }

      const source = this.audioContext.createBufferSource()
      source.buffer = audioBuffer
      source.connect(this.audioContext.destination)

      const now = this.audioContext.currentTime
      if (this.nextPlayTime < now) {
        this.nextPlayTime = now + 0.05
      }

      source.start(this.nextPlayTime)
      this.nextPlayTime += audioBuffer.duration
    } catch (error) {
      console.error('播放 chunk 失败:', error)
    }
  }

  stop(): void {
    try {
      this.audioContext?.close()
    } catch { }
    this.audioContext = null
    this.nextPlayTime = 0
  }
}

// =============================================================================
// 主组件
// =============================================================================

export default function LiveTranslateBidirectional() {
  const [isRunning, setIsRunning] = useState(false)
  const [status, setStatus] = useState('就绪')
  const [error, setError] = useState<string | null>(null)

  const [sessionStartTime, setSessionStartTime] = useState(0)

  const [lang1, setLang1] = useState('zh-Hans')
  const [lang2, setLang2] = useState('ja')

  const [history, setHistory] = useState<HistoryRecord[]>([])
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const historyEndRef = useRef<HTMLDivElement>(null)

  const ws1Ref = useRef<WebSocket | null>(null)
  const ws2Ref = useRef<WebSocket | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const captureCtxRef = useRef<AudioContext | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const playerRef = useRef<TranslateAudioPlayer | null>(null)
  const isRunningRef = useRef(false)

  useEffect(() => {
    playerRef.current = new TranslateAudioPlayer()
    return () => {
      stopTranslation()
      playerRef.current?.stop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isHistoryOpen) {
      historyEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [history, isHistoryOpen])

  const getWsUrl = useCallback((lang: string): string => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const query = `?mode=translate&target_lang=${encodeURIComponent(lang)}`

    if (window.location.protocol === 'https:') {
      return `wss://${window.location.host}/ws/chat${query}`
    }
    return `${protocol}//${window.location.hostname}:8080/ws/chat${query}`
  }, [])

  const startAudioCapture = useCallback(async (ws1: WebSocket, ws2: WebSocket) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      })
      mediaStreamRef.current = stream

      if (!isRunningRef.current) {
        stream.getTracks().forEach(t => t.stop())
        return
      }

      const AC = window.AudioContext || (window as any).webkitAudioContext
      const captureCtx = new AC({ sampleRate: 16000 })
      captureCtxRef.current = captureCtx

      const source = captureCtx.createMediaStreamSource(stream)
      const scriptProcessor = captureCtx.createScriptProcessor(4096, 1, 1)
      processorRef.current = scriptProcessor

      scriptProcessor.onaudioprocess = (e) => {
        if (!isRunningRef.current) return

        const inputData = e.inputBuffer.getChannelData(0)
        const pcm16 = new Int16Array(inputData.length)
        for (let i = 0; i < inputData.length; i++) {
          let s = Math.max(-1, Math.min(1, inputData[i]))
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
        }

        try {
          if (ws1.readyState === WebSocket.OPEN) ws1.send(pcm16.buffer)
          if (ws2.readyState === WebSocket.OPEN) ws2.send(pcm16.buffer)
        } catch (err) { }
      }

      const silenceGain = captureCtx.createGain()
      silenceGain.gain.value = 0
      source.connect(scriptProcessor)
      scriptProcessor.connect(silenceGain)
      silenceGain.connect(captureCtx.destination)

    } catch (err: any) {
      setError(`麦克风初始化失败: ${err.message}`)
      stopTranslation()
    }
  }, [])

  const createMessageHandler = (channelId: number) => (event: MessageEvent) => {
    if (event.data instanceof ArrayBuffer) {
      playerRef.current?.playChunk(event.data)
      return
    }

    try {
      const msg = JSON.parse(event.data) as TranscriptionMessage
      if (msg.type === 'transcription') {
        const inp = msg.input_transcription || ''
        const out = msg.output_transcription || ''

        if (out || inp) {
          setHistory(prev => {
            const newHist = [...prev]

            // 独立寻找属于【当前频道】的最后一条记录
            let lastChannelIdx = -1
            for (let i = newHist.length - 1; i >= 0; i--) {
              if (newHist[i].channelId === channelId) {
                lastChannelIdx = i
                break
              }
            }
            const lastItem = lastChannelIdx >= 0 ? newHist[lastChannelIdx] : null

            const now = Date.now()

            // 只要同频道的停顿不超过 10秒，或者文字有继承关系，就无缝拼接
            if (
              lastItem &&
              (now - lastItem.timestamp < 10000 || out.startsWith(lastItem.outputText))
            ) {
              // 1. 处理“幽灵原话残留”
              const wasPhantom = lastItem.outputText === ''
              const finalInputText = wasPhantom && inp ? inp : mergeText(lastItem.inputText, inp)
              const finalOutputText = mergeText(lastItem.outputText, out)

              const updatedItem = {
                ...lastItem,
                inputText: finalInputText,
                outputText: finalOutputText,
                timestamp: now,
              }

              // 2. 核心时序修复：冒泡机制
              // 如果它是一个占位符，且现在终于拿到了翻译，我们必须把它移到数组最后，保证时间线顺畅！
              if (wasPhantom && finalOutputText !== '') {
                newHist.splice(lastChannelIdx, 1) // 拔出
                newHist.push(updatedItem)         // 放回末尾
              } else {
                newHist[lastChannelIdx] = updatedItem // 原地更新
              }

              return newHist
            }

            // 新起一句话
            newHist.push({
              id: Math.random().toString(36).substring(2, 9),
              channelId,
              inputText: inp,
              outputText: out,
              timestamp: now,
            })
            return newHist
          })
        }
      } else if ((msg as any).type === 'error') {
        setError(`通道 ${channelId} 错误: ${(msg as any).message}`)
      }
    } catch (err) { }
  }

  const startTranslation = async () => {
    if (isRunning) return
    setError(null); setStatus('连接中...'); setIsRunning(true); isRunningRef.current = true
    setSessionStartTime(Date.now())

    try {
      await playerRef.current?.unlock()

      const ws1 = new WebSocket(getWsUrl(lang1))
      ws1.binaryType = 'arraybuffer'
      ws1Ref.current = ws1
      ws1.onmessage = createMessageHandler(1)

      const ws2 = new WebSocket(getWsUrl(lang2))
      ws2.binaryType = 'arraybuffer'
      ws2Ref.current = ws2
      ws2.onmessage = createMessageHandler(2)

      let connectedCount = 0
      const checkReady = () => {
        connectedCount++
        if (connectedCount === 2) {
          setStatus('双向同传进行中...')
          startAudioCapture(ws1, ws2)
        }
      }

      ws1.onopen = checkReady; ws2.onopen = checkReady
      ws1.onclose = () => stopTranslation(); ws2.onclose = () => stopTranslation()
      ws1.onerror = () => { setError('通道 1 连接失败'); stopTranslation() }
      ws2.onerror = () => { setError('通道 2 连接失败'); stopTranslation() }

    } catch (err: any) {
      setError(`启动失败: ${err.message}`)
      stopTranslation()
    }
  }

  const stopTranslation = useCallback(() => {
    isRunningRef.current = false; setIsRunning(false); setStatus('已停止')
    if (processorRef.current) {
      processorRef.current.disconnect()
      processorRef.current.onaudioprocess = null
      processorRef.current = null
    }
    try { captureCtxRef.current?.close() } catch { }
    mediaStreamRef.current?.getTracks().forEach(t => t.stop())
    if (ws1Ref.current?.readyState === WebSocket.OPEN) ws1Ref.current.close()
    if (ws2Ref.current?.readyState === WebSocket.OPEN) ws2Ref.current.close()
    playerRef.current?.stop(); playerRef.current = new TranslateAudioPlayer()
  }, [])

  // ---------------------------------------------------------------------------
  // UI 渲染处理：仅显示具有真实翻译输出的内容
  // ---------------------------------------------------------------------------

  const validHistory = history.filter(h => h.outputText.trim() !== '')

  const ch1Active = validHistory.filter(h => h.channelId === 1 && h.timestamp >= sessionStartTime)
  const text1Display = ch1Active.length > 0 ? ch1Active[ch1Active.length - 1].outputText : ''

  const ch2Active = validHistory.filter(h => h.channelId === 2 && h.timestamp >= sessionStartTime)
  const text2Display = ch2Active.length > 0 ? ch2Active[ch2Active.length - 1].outputText : ''

  return (
    <div className="min-h-screen w-full bg-slate-50 flex flex-col items-center p-4 overflow-hidden">

      <div className="w-full max-w-5xl flex justify-between items-start mt-4">
        <a href="/" className="inline-flex items-center text-indigo-500 text-sm font-medium hover:underline bg-white px-4 py-2 rounded-full shadow-sm border border-slate-100">
          ← 返回首页
        </a>
        <button
          onClick={() => setIsHistoryOpen(true)}
          className="inline-flex items-center gap-2 bg-white px-5 py-2.5 rounded-full shadow-sm border border-slate-100 text-slate-700 font-bold hover:shadow-md transition-all active:scale-95"
        >
          <span>📜</span> 查看历史记录
          {validHistory.length > 0 && (
            <span className="bg-indigo-500 text-white text-xs px-2 py-0.5 rounded-full">{validHistory.length}</span>
          )}
        </button>
      </div>

      <div className="w-full max-w-4xl mt-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-slate-800">🌐 Talk-o 双向同传</h1>
          <p className="text-slate-500 mt-2 font-medium">双向通道并发互译，适合两种语言实时对话场景</p>
        </div>

        {error && <div className="mb-6 p-4 bg-rose-100 text-rose-700 rounded-xl text-center shadow-sm font-medium border border-rose-200">{error}</div>}

        <div className="flex flex-col items-center justify-center mb-10">
          <button
            onClick={isRunning ? stopTranslation : startTranslation}
            className={`w-32 h-32 rounded-full text-white shadow-xl transition-all duration-300 ${isRunning ? 'bg-rose-500 animate-pulse shadow-rose-200' : 'bg-sky-500 hover:scale-105 shadow-sky-200'}`}
          >
            <div className="text-2xl mb-1">{isRunning ? '■' : '▶'}</div>
            <div className="font-bold text-lg">{isRunning ? '停止' : '开始同传'}</div>
          </button>
          <div className="mt-6 text-sm font-bold text-slate-500 bg-white px-4 py-1.5 rounded-full shadow-sm border border-slate-100">
            状态: <span className={isRunning ? 'text-emerald-500' : 'text-slate-400'}>{status}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-200 relative overflow-hidden">
            <div className={`absolute top-0 left-0 w-full h-1 ${isRunning && text1Display ? 'bg-indigo-500' : 'bg-transparent'} transition-colors duration-500`}></div>
            <h2 className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-indigo-500"></span> 语言 A 频道
            </h2>
            <select value={lang1} disabled={isRunning} onChange={e => setLang1(e.target.value)} className="w-full p-2.5 bg-slate-50 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 mb-4 focus:ring-2 focus:ring-indigo-200 outline-none">
              {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <div className="h-32 p-4 bg-slate-50/80 rounded-2xl overflow-y-auto text-slate-700 font-medium border border-slate-100 shadow-inner">
              {text1Display || <span className="text-slate-400 text-sm italic">等待翻译流...</span>}
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-200 relative overflow-hidden">
            <div className={`absolute top-0 left-0 w-full h-1 ${isRunning && text2Display ? 'bg-purple-500' : 'bg-transparent'} transition-colors duration-500`}></div>
            <h2 className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-purple-500"></span> 语言 B 频道
            </h2>
            <select value={lang2} disabled={isRunning} onChange={e => setLang2(e.target.value)} className="w-full p-2.5 bg-slate-50 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 mb-4 focus:ring-2 focus:ring-purple-200 outline-none">
              {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <div className="h-32 p-4 bg-slate-50/80 rounded-2xl overflow-y-auto text-slate-700 font-medium border border-slate-100 shadow-inner">
              {text2Display || <span className="text-slate-400 text-sm italic">等待翻译流...</span>}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`fixed inset-0 z-50 transition-all duration-300 ${isHistoryOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
      >
        <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={() => setIsHistoryOpen(false)}></div>
        <div className={`absolute top-0 right-0 w-full max-w-md h-full bg-white shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col ${isHistoryOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex justify-between items-center p-6 border-b border-slate-100">
            <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">📜 翻译历史</h2>
            <div className="flex items-center gap-3">
              <button onClick={() => { if (window.confirm('确定要清空所有记录吗？')) setHistory([]) }} className="text-xs font-bold text-rose-500 hover:text-rose-600 bg-rose-50 px-3 py-1.5 rounded-full transition-colors">清空</button>
              <button onClick={() => setIsHistoryOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors">✕</button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50 space-y-6">
            {validHistory.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-3">
                <span className="text-4xl">📭</span>
                <p className="text-sm font-medium">暂无翻译记录</p>
              </div>
            ) : (
              validHistory.map((item) => {
                const isCh1 = item.channelId === 1
                const langLabel = isCh1 ? LANGUAGES.find(l => l.code === lang1)?.label : LANGUAGES.find(l => l.code === lang2)?.label

                return (
                  <div key={item.id} className={`flex flex-col ${isCh1 ? 'items-start' : 'items-end'}`}>
                    <div className="flex items-center gap-2 mb-1 px-1">
                      <span className={`w-2 h-2 rounded-full ${isCh1 ? 'bg-indigo-400' : 'bg-purple-400'}`}></span>
                      <span className="text-xs font-bold text-slate-400">{langLabel}</span>
                    </div>
                    <div className={`max-w-[85%] rounded-2xl p-4 shadow-sm border ${isCh1 ? 'bg-white border-indigo-100 rounded-tl-none' : 'bg-purple-50 border-purple-100 rounded-tr-none'
                      }`}>
                      {item.inputText && (
                        <p className="text-xs text-slate-400 font-medium mb-1.5 pb-1.5 border-b border-slate-100/50">
                          原话: {item.inputText}
                        </p>
                      )}
                      <p className="text-sm font-bold text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {item.outputText}
                      </p>
                    </div>
                  </div>
                )
              })
            )}
            <div ref={historyEndRef}></div>
          </div>
        </div>
      </div>

    </div>
  )
}